import React, { useState } from "react";
import { Box, Typography, Collapse, Grid, IconButton } from "@mui/material";
import CircleIcon from "@mui/icons-material/Circle";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import StatusBadge from "./StatusBadge";
import { TEXT_COLOR } from "../pages/root";

const STATUS_CONFIG = {
  ok: {
    text: "Funcional",
    color: "#4caf50",
    bgColor: "white",
    detail: "Todos los servicios funcionan correctamente",
  },
  degraded: {
    text: "Con fallas",
    color: "#ff9800",
    bgColor: "#fff8e1",
    detail: "Algunos servicios presentan problemas",
  },
  fail: {
    text: "No funcional",
    color: "#f44336",
    bgColor: "#ffebee",
    detail: "El sistema no está disponible",
  },
  unknown: {
    text: "Desconocido",
    color: "#9e9e9e",
    bgColor: "#f5f5f5",
    detail: "No se pudo determinar el estado del sistema",
  },
};

const ServiceItem = ({ name, up, version }) => (
  <Box sx={{ display: "flex", flexDirection: "column", alignItems: "start", mb: 1 }}>
    <Box sx={{ display: "flex", alignItems: "center" }}>
      <CircleIcon sx={{ fontSize: 10, color: up ? "#4caf50" : "#f44336", mr: 1 }} />
      <Typography variant="body2">{name}</Typography>
    </Box>
    {version && (
      <Typography variant="caption" color="textSecondary" sx={{ ml: 2 }}>
        v{version}
      </Typography>
    )}
  </Box>
);

const SystemStatusIndicator = ({ health, loading, error }) => {
  const [open, setOpen] = useState(false);

  if (loading) {
    return <StatusBadge color="#ffc107" text="Consultando estado..." />;
  }

  if (error || !health) {
    return <StatusBadge color="#f44336" text="Sistema fuera de línea" />;
  }

  const statusKey = STATUS_CONFIG[health.status] ? health.status : "unknown";
  const status = STATUS_CONFIG[statusKey];

  const services = [
    {
      name: "Frontend",
      up: true,
      // Versión del frontend
      version: "3.2.9",
    },
    {
      name: "Backend",
      up: health.backend?.healthy,
      version: health.backend?.version,
    },
    { name: "PostgreSQL", up: health.postgres },
    { name: "MongoDB", up: health.mongo },
  ];

  return (
    <Box sx={{ position: "relative" }}>
      <Box
        onClick={() => setOpen(!open)}
        sx={{
          display: "flex",
          alignItems: "center",
          cursor: "pointer",
          backgroundColor: "rgba(250, 235, 188, 0.8)",
          borderRadius: 1,
          px: 1.5,
          py: 0.5,
          transition: "background-color 0.2s",
          "&:hover": { backgroundColor: "rgba(239, 227, 190, 0.8)" },
        }}
      >
        <CircleIcon sx={{ fontSize: 10, color: status.color, mr: 1 }} />
        <Typography variant="caption" color={TEXT_COLOR} sx={{ fontWeight: 500 }}>
          {status.text}
        </Typography>
        <ExpandMoreIcon
          fontSize="small"
          sx={{
            color: TEXT_COLOR,
            ml: 0.5,
            transform: open ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 0.2s",
          }}
        />
      </Box>

      <Collapse in={open} sx={{ position: "absolute", top: "100%", right: 0, zIndex: 1300, width: 320 }}>
        <Box sx={{ backgroundColor: "white", color: "text.primary", boxShadow: 3, borderRadius: 1, border: "1px solid #e0e0e0", mt: 1, p: 3, pl: 4 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
            <Typography variant="h6" fontWeight="bold">
              Estado del sistema
            </Typography>
            <IconButton size="small" onClick={() => setOpen(false)}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          {/* Estado general */}
          <Box sx={{ mb: 2, p: 1.5, borderRadius: 1, backgroundColor: status.bgColor }}>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Box sx={{ width: 10, height: 10, borderRadius: "50%", backgroundColor: status.color, mr: 1.5 }} />
              <Typography variant="body1" fontWeight="medium">
                Estado general: <span style={{ color: status.color }}>{status.text}</span>
              </Typography>
            </Box>
            <Typography variant="body2" color="textSecondary" sx={{ mt: 1, ml: 2.5 }}>
              {status.detail}
            </Typography>
          </Box>

          {/* Estado de los servicios */}
          <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
            Servicios
          </Typography>
          <Grid container spacing={1.5}>
            {services.map((service) => (
              <Grid item xs={6} key={service.name}>
                <ServiceItem {...service} />
              </Grid>
            ))}
          </Grid>

          <Typography variant="caption" color="textSecondary" sx={{ display: "block", mt: 2, textAlign: "right" }}>
            Última actualización: {new Date().toLocaleTimeString()}
          </Typography>
        </Box>
      </Collapse>
    </Box>
  );
};

export default SystemStatusIndicator;
